import React, { useEffect, useState } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { ref as rtdbRef, onValue } from 'firebase/database';
import { rtdb } from '../../config/firebase';
import { Box, Typography, TextField, List, ListItem, ListItemButton, ListItemText, Avatar, Divider, CircularProgress, Tooltip } from '@mui/material';
import type { VideoStats } from '../../types/user';
import UserManagementDialog from './UserManagementDialog';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  name: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  platformLogins?: { booking?: number; video?: number };
  videoStats?: VideoStats;
  role?: string;
  userTotalVideosStarted?: number;
  userTotalVideosCompleted?: number;
  totalWatchtime?: number; // Total watchtime in seconds
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Add online status state
  const [userStatus, setUserStatus] = useState<{ [userId: string]: 'online' | 'offline' }>({});
  const { t } = useTranslation();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const snap = await getDocs(collection(db, 'users'));
      const userList: User[] = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
      setUsers(userList);
      setLoading(false);
    };
    fetchUsers();
  }, []);

  // Listen for real-time status for all users
  useEffect(() => {
    const listeners: Array<() => void> = [];
    users.forEach((user) => {
      if (!user.id) return;
      const statusRef = rtdbRef(rtdb, `/status/${user.id}`);
      const unsubscribe = onValue(statusRef, (snapshot) => {
        setUserStatus((prev) => ({ ...prev, [user.id]: snapshot.val() === 'online' ? 'online' : 'offline' }));
      });
      listeners.push(() => unsubscribe());
    });
    return () => {
      listeners.forEach((unsub) => unsub());
    };
  }, [users]);

  // Group users by email, merge roles
  const emailMap: { [email: string]: User & { roles: string[] } } = {};
  users.forEach(u => {
    if (!u.email) return;
    if (!emailMap[u.email]) {
      emailMap[u.email] = { ...u, roles: [u.role || ''] };
    } else {
      if (u.role && !emailMap[u.email].roles.includes(u.role)) {
        emailMap[u.email].roles.push(u.role);
      }
    }
  });
  const mergedUsers = Object.values(emailMap);

  const filteredUsers = mergedUsers.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    // Refresh user list after potential updates
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, 'users'));
      const userList: User[] = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
      setUsers(userList);
    };
    fetchUsers();
  };

  return (
    <Box sx={{ 
      p: 3, 
      background: 'transparent',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TextField
        label={t('admin.searchByNameOrEmail')}
        value={search}
        onChange={e => setSearch(e.target.value)}
        fullWidth
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(227, 28, 37, 0.2)',
            borderRadius: 2,
            '& fieldset': {
              border: 'none'
            },
            '&:hover fieldset': {
              border: 'none'
            },
            '&.Mui-focused fieldset': {
              border: '1px solid #E31C25'
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.8)',
            '&.Mui-focused': {
              color: '#E31C25'
            }
          },
          '& .MuiOutlinedInput-input': {
            color: 'white',
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.6)'
            }
          }
        }}
      />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#E31C25' }} />
        </Box>
      ) : (
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          maxHeight: 280,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E31C25',
            borderRadius: '3px',
            '&:hover': {
              background: '#ff4757',
            },
          },
        }}>
          <List sx={{ p: 0 }}>
            {filteredUsers.map(user => (
              <React.Fragment key={user.email}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleUserClick(user)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(227, 28, 37, 0.1)',
                        transform: 'translateX(4px)',
                        '& .MuiListItemText-primary': {
                          color: '#E31C25'
                        }
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', mr: 2 }}>
                      <Avatar 
                        src={user.photoURL} 
                        sx={{
                          width: 40,
                          height: 40,
                          border: '2px solid rgba(227, 28, 37, 0.3)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      />
                      <Tooltip title={userStatus[user.id] === 'online' ? t('common.online') : t('common.offline')} placement="bottom">
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: userStatus[user.id] === 'online' ? '#4CAF50' : '#E31C25',
                            border: '2px solid rgba(45, 45, 45, 0.95)',
                            boxShadow: '0 0 0 1px rgba(227, 28, 37, 0.3)',
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            fontSize: '0.9rem'
                          }}
                        >
                          {user.name || user.displayName || user.email}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.8rem',
                            mt: 0.5
                          }}
                        >
                          {user.roles?.filter(Boolean).join(', ')}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ 
                  borderColor: 'rgba(227, 28, 37, 0.1)',
                  mx: 2
                }} />
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      <UserManagementDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        user={selectedUser}
      />
    </Box>
  );
};

export default UserManagement;
